/**
 * @lines98/client/lib/settings-list — the SINGLE declarative source of the app's
 * settings rows (extra-modes §5.5 / T-39R-B3).
 *
 * «Ствол → ветки» (CLAUDE.md «код через модули»): the list of settings lives ONCE
 * here and is mounted by BOTH the settings-drawer (`components/settings-drawer`,
 * the primary entry from the nav-rail «Настройки», works during a game) AND the
 * legacy settings-screen (`?demo=settings` viewport baseline). Adding a setting
 * (дальтоники / темы / звук — БУДУЩЕЕ) = one more `createSettingRow(...)` here, no
 * duplication across surfaces.
 *
 * The source of truth for the value is `lib/client-prefs` (versioned, persisted);
 * each control reads the current value on build + writes via `setPref` on change.
 */

import { createSettingRow, type SettingRowHandle } from '../components/setting-row.js';
import { createThemeSwitch } from '../components/theme-switch.js';
import { createToggleSwitch } from '../components/toggle-switch.js';
import { getClientPrefs, setPref } from './client-prefs.js';
import { t } from './i18n/index.js';
import { getTheme, setTheme } from './theme.js';

/**
 * Build the settings rows (one source for the drawer + the screen). The caller
 * appends `row.element` into its list container and calls `row.destroy()` on
 * unmount (releases the row's `?`-popover).
 */
export function buildSettingRows(): SettingRowHandle[] {
  const prefs = getClientPrefs();

  // --- Live-move animation toggle (brief-39 §B; default OFF) ---
  const liveAnimToggle = createToggleSwitch({
    checked: prefs.liveMoveAnimation,
    ariaLabel: t('game.settingsScreen.liveAnimAria'),
    onChange: (checked) => setPref('liveMoveAnimation', checked),
  });
  const liveAnimRow = createSettingRow({
    label: t('game.settingsScreen.liveAnimLabel'),
    description: t('game.settingsScreen.liveAnimDesc'),
    control: liveAnimToggle.element,
  });

  // --- Блок добычи меты на экране итогов (Срез 1b; default ON) ---
  // owner-decision 2026-06-05 (meta-jeweler-surfaces.md §4.1): экран добычи можно
  // выключить. Выкл → награды всё равно начисляются сервером (ничего не теряется),
  // просто без анимированного блока — для тех, кому мета не нужна / нужна скорость.
  const rewardToggle = createToggleSwitch({
    checked: prefs.showRewardScreen,
    ariaLabel: t('game.settingsScreen.rewardAria'),
    onChange: (checked) => setPref('showRewardScreen', checked),
  });
  const rewardRow = createSettingRow({
    label: t('game.settingsScreen.rewardLabel'),
    description: t('game.settingsScreen.rewardDesc'),
    control: rewardToggle.element,
  });

  // --- Тема приложения (§5.5; сегмент ☀/🌙, только иконки) ---
  const themeSwitch = createThemeSwitch({
    value: getTheme(),
    onChange: (theme) => setTheme(theme),
  });
  const themeRow = createSettingRow({
    label: t('game.settingsScreen.themeLabel'),
    description: t('game.settingsScreen.themeDesc'),
    control: themeSwitch.element,
  });

  // --- Режим для дальтоников (§5.5; ПРИСУТСТВУЕТ, но НЕАКТИВЕН — задел) ---
  const colorBlindToggle = createToggleSwitch({
    checked: false,
    disabled: true,
    ariaLabel: t('game.settingsScreen.colorBlindAria'),
    onChange: () => {
      /* disabled — no-op (плейсхолдер до реализации палитры) */
    },
  });
  const colorBlindRow = createSettingRow({
    label: t('game.settingsScreen.colorBlindLabel'),
    description: t('game.settingsScreen.colorBlindDesc'),
    control: colorBlindToggle.element,
  });

  return [liveAnimRow, rewardRow, themeRow, colorBlindRow];
}
